import React, { useState, useEffect } from 'react';
import { Page, Layout, LegacyCard, Box, Button, InlineStack, Text, Listbox, Card, Link, Icon, InlineGrid, Grid, BlockStack, SkeletonPage, SkeletonDisplayText, SkeletonBodyText, TextContainer } from '@shopify/polaris';
import { AppExtensionIcon, DiscountIcon, ProductFilledIcon, ProductIcon, CartIcon, DeliveryIcon } from '@shopify/polaris-icons';

import { SkeletonPageCustom } from './SkeletonPageCustom';
import { FetchCustom } from './FetchCustom';
import { useHostNavigate } from "../hooks";

export function Widgets(props) {
    const navigate = useHostNavigate();

    const [loading, setLoading] = useState(true);
    const [model, setModel] = useState({});
    const [redirectUrl, setRedirectUrl] = useState('');

    const fetch = FetchCustom();

    const populateData = async () => {
        props.setIsLoading(true);
        const data = await fetch('api/home/widgets');

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setModel(data);
            setLoading(false);
        }
    };

    const getUser = async () => {
        if (!props.userLoaded) {
            props.setUserLoaded(true);
            const data = await fetch('api/home/userInfo');

            if (data.redirectUrl) {
                setRedirectUrl(data.redirectUrl);
            }
            else if (JSON.stringify(props.user) !== JSON.stringify(data)) {
                props.setUser(data);
                localStorage.setItem('user', JSON.stringify(data));
            }
        }
    };

    useEffect(() => {
        getUser();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (JSON.stringify(props.user) !== '{}') {
            document.title = "Widgets | Stack Discounts";
            populateData();
        }
        // eslint-disable-next-line
    }, [props.user]);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);

    const navigation = (
        <div className="listbox-navigation">
            <Card padding="100">
                <Box paddingBlock="100">
                    <Listbox onSelect={(value) => { navigate(value); }}>
                        <Listbox.Action value="/widgets" divider selected={true}>
                            <InlineStack gap="200">
                                <Icon source={AppExtensionIcon} tone="primary" />
                                <Text fontWeight="medium">General settings</Text>
                            </InlineStack>
                        </Listbox.Action>
                        <Listbox.Action value="/widgets/sticky">
                            <InlineStack gap="200">
                                <Icon source={DiscountIcon} tone="primary" />
                                <Text fontWeight="medium">Sticky widget</Text>
                            </InlineStack>
                        </Listbox.Action>
                        <Listbox.Action value="/widgets/product-page">
                            <InlineStack gap="200">
                                <Icon source={ProductIcon} tone="primary" />
                                <Text fontWeight="medium">Product Page widget</Text>
                            </InlineStack>
                        </Listbox.Action>
                        <Listbox.Action value="/widgets/cart-page">
                            <InlineStack gap="200">
                                <Icon source={CartIcon} tone="primary" />
                                <Text fontWeight="medium">Cart Page widget</Text>
                            </InlineStack>
                        </Listbox.Action>
                        <Listbox.Action value="/widgets/shipping-bar">
                            <InlineStack gap="200">
                                <Icon source={DeliveryIcon} tone="primary" />
                                <Text fontWeight="medium">Shipping Bar widget</Text>
                            </InlineStack>
                        </Listbox.Action>
                    </Listbox>
                </Box>
            </Card>
        </div>
    );

    return (
        loading ?
            <SkeletonPage>
                <Layout>
                    <Layout.Section variant="oneThird">
                        {navigation}
                    </Layout.Section>
                    <Layout.Section>
                        <LegacyCard sectioned>
                            <TextContainer>
                                <SkeletonDisplayText size="small" />
                                <SkeletonBodyText lines={9} />
                            </TextContainer>
                        </LegacyCard>
                    </Layout.Section>
                </Layout>
            </SkeletonPage>
            :
            <Page title="Widgets">
                <Layout>
                    <Layout.Section variant="oneThird">
                        {navigation}
                    </Layout.Section>
                    <Layout.Section>
                        <BlockStack gap="400">
                            <Card roundedAbove="sm">
                                <Text as="h2" variant="headingSm">
                                    General settings
                                </Text>
                            </Card>
                            <Card roundedAbove="sm">
                                <Text as="h2" variant="headingSm">
                                    Widgets
                                </Text>
                                <Box paddingBlockStart="400">
                                    <Grid columns={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}>
                                        <Grid.Cell>
                                            <Card padding="0">
                                                <div className="widget-item" onClick={() => { navigate('/widgets/sticky'); }}>
                                                    <div><Icon source={DiscountIcon} tone="primary" /></div>
                                                    <div>
                                                        <div><Text fontWeight="medium">Sticky Widget</Text></div>
                                                        <div><Text variant="bodySm" as="p">Present all discounts sitewide</Text></div>
                                                    </div>
                                                </div>
                                            </Card>
                                        </Grid.Cell>
                                        <Grid.Cell>
                                            <Card padding="0">
                                                <div className="widget-item" onClick={() => { navigate('/widgets/product-page'); }}>
                                                    <div><Icon source={ProductIcon} tone="primary" /></div>
                                                    <div>
                                                        <div><Text fontWeight="medium">Product Page Widget</Text></div>
                                                        <div><Text variant="bodySm" as="p">Product related discounts</Text></div>
                                                    </div>
                                                </div>
                                            </Card>
                                        </Grid.Cell>
                                        <Grid.Cell>
                                            <Card padding="0">
                                                <div className="widget-item" onClick={() => { navigate('/widgets/cart-page'); }}>
                                                    <div><Icon source={CartIcon} tone="primary" /></div>
                                                    <div>
                                                        <div><Text fontWeight="medium">Cart Page Widget</Text></div>
                                                        <div><Text variant="bodySm" as="p">Enable discount input</Text></div>
                                                    </div>
                                                </div>
                                            </Card>
                                        </Grid.Cell>
                                        <Grid.Cell>
                                            <Card padding="0">
                                                <div className="widget-item" onClick={() => { navigate('/widgets/shipping-bar'); }}>
                                                    <div><Icon source={DeliveryIcon} tone="primary" /></div>
                                                    <div>
                                                        <div><Text fontWeight="medium">Shipping Bar Widget</Text></div>
                                                        <div><Text variant="bodySm" as="p">Present shipping discounts</Text></div>
                                                    </div>
                                                </div>
                                            </Card>
                                        </Grid.Cell>
                                    </Grid>
                                </Box>
                            </Card>
                        </BlockStack>
                    </Layout.Section>
                </Layout>
            </Page>
    );
}